import { get, post, del, download } from './client.js';

/**
 * accountsApi — 帳號管理（商用帳號）client，dealer / super_admin 共用。
 * 後端 /api/ivr/saas-admin/* 依 X-User-Uid 做 role gate：
 *   - 經銷商打 listBusiness → 只回自己開的 business
 *   - 經銷商打 create → createdBy 強制 = 自己
 *   - 經銷商打 cloneAvatars → 只能對自己開的 business + 自己 tenant 底下的角色
 */
export const accountsApi = {
    // 商用帳號清單（含席次 + 角色用量）
    listBusiness: () => get('/api/ivr/saas-admin/parents?role=business&seatUsage=1'),
    // 一次到位開戶（帳號 + tenant + 席次 + roleQuota）
    create: (payload) => post('/api/ivr/saas-admin/parents', payload),
    // 複製角色（avatar）到商用帳號旗下 tenant — 立即回 jobId，背景跑
    cloneAvatars: (uid, body) =>
        post(`/api/ivr/saas-admin/parents/${encodeURIComponent(uid)}/clone-avatars`, body),
    // 查 clone 背景進度
    cloneJobStatus: (jobId) =>
        get(`/api/ivr/saas-admin/clone-jobs/${encodeURIComponent(jobId)}`),
    // 軟刪商用帳號（dealer 只能刪自己開的）
    deleteBusiness: (uid) =>
        del(`/api/ivr/saas-admin/parents/${encodeURIComponent(uid)}`),
    // 經銷商名下（含旗下商用帳號）的租戶清單 — 「共用既有租戶」開商用子帳號時選用
    dealerTenants: () => get('/api/ivr/saas-admin/dealer-tenants'),
    // 某 tenant 底下的 avatar 清單（給「選要複製的角色」用）
    tenantAvatars: (hostId) =>
        get(`/api/ivr/admin/tenant-avatars?hostId=${encodeURIComponent(hostId)}`),

    /**
     * 商用帳號月報表 xlsx（表頭 + 每日彙總 + 每角色一分頁；不含通話明細）
     *
     * ⚠️ 要用 download() 帶 token 抓，不能用 `<a href download>`（不帶 token → proxy 401）。
     * hostIds 只縮小明細範圍 —— 表頭的免費額度與超出分鐘一律以帳號全部租戶合計計算。
     *
     * @param {string} uid   商用帳號 uid
     * @param {string} month 'YYYY-MM'
     * @param {{ hostIds?: string[], lang?: string }} opts
     */
    downloadReport: (uid, month, { hostIds, lang } = {}) => {
        const qs = new URLSearchParams({ month });
        if (hostIds && hostIds.length) qs.set('hostIds', hostIds.join(','));
        if (lang) qs.set('lang', lang);
        return download(`/api/ivr/saas-admin/accounts/${encodeURIComponent(uid)}/report.xlsx?${qs.toString()}`);
    },

    /**
     * 經銷商整批報表 xlsx — 旗下每個商用帳號一張分頁（總覽 + 各帳號每日彙總）。
     *
     * ⚠️ 慢：實測最大的經銷商 33 個帳號要 25 秒（99% 是 Firestore 掃描），後端有 10 分鐘快取，
     * 但第一次下載一定會等 —— 呼叫端務必給 loading 狀態，不然使用者會以為當掉又按一次。
     * 不含 AI 角色分頁（那是客戶自己那份才有）。
     */
    downloadDealerReport: (uid, month, { lang } = {}) => {
        const qs = new URLSearchParams({ month });
        if (lang) qs.set('lang', lang);
        return download(`/api/ivr/saas-admin/dealers/${encodeURIComponent(uid)}/report.xlsx?${qs.toString()}`);
    },
};
